/**
 * Pendo Tracking Utility for Cross and Legal Reference Word Add-in
 * 
 * This utility provides functions to track user interactions with the Word Add-in
 * using Pendo analytics.
 * 
 * Event Names (with WordAddin prefix to distinguish from CKEditor events):
 * - WordAddin-CrossandLegalReference-CreateReference: When user adds existing references (from Legal Reference tab or Policies tab)
 * - WordAddin-CrossandLegalReference-Addon: When user creates a new legal reference
 * - WordAddin-CrossandLegalReference-DeleteReference: When user deletes an inline reference from the document
 */

// Pendo Event Names - Prefixed with "WordAddin-" to distinguish from CKEditor events
export const PENDO_EVENTS = {
  CREATE_REFERENCE: 'WordAddin-CrossandLegalReference-CreateReference',
  ADDON: 'WordAddin-CrossandLegalReference-Addon',
  DELETE_REFERENCE: 'WordAddin-CrossandLegalReference-DeleteReference',
};

/**
 * Check if Pendo is available in the global scope
 * @returns {boolean} - True if Pendo is available
 */
const isPendoAvailable = () => {
  return typeof window !== 'undefined' && window.pendo && typeof window.pendo.track === 'function';
};

/**
 * Check if Pendo initialize is available
 * @returns {boolean} - True if Pendo initialize is available
 */
const isPendoInitializeAvailable = () => {
  return typeof window !== 'undefined' && window.pendo && typeof window.pendo.initialize === 'function';
};

/**
 * Initialize Pendo with visitor and account information
 * Should be called after user authentication is successful
 * @param {Object} userData - User data from authentication
 * @param {string} userData.username - Username or unique identifier
 * @param {string} userData.email - User email (optional)
 * @param {Object} organizationData - Organization data (optional)
 * @param {string} organizationData.name - Organization name
 * @param {string} organizationData.id - Organization ID
 */
export const initializePendo = (userData = {}, organizationData = {}) => {
  try {
    if (!isPendoInitializeAvailable()) {
      console.log("[PENDO-WORDADDIN] ⚠️ Pendo not available for initialization");
      return false;
    }

    // Build visitor ID from hostname and username
    const hostname = window.location.hostname || 'unknown-host';
    const username = userData.username || userData.email || 'anonymous';
    const visitorId = `${hostname}-WordAddin-${username}`;

    // Build account ID
    const accountId = organizationData.id || organizationData.name || hostname;

    const pendoConfig = {
      visitor: {
        id: visitorId,
        email: userData.email || '',
        username: userData.username || '',
        // Word Add-in specific properties
        WordAddin_User: true,
        WordAddin_Platform: Office?.context?.platform || 'unknown',
      },
      account: {
        id: `${accountId}-WordAddin`,
        name: organizationData.name || 'Unknown Organization',
        // Word Add-in specific properties
        WordAddin_Account: true,
      }
    };

    window.pendo.initialize(pendoConfig);
    console.log("[PENDO-WORDADDIN] ✅ Pendo initialized successfully:", {
      visitorId: pendoConfig.visitor.id,
      accountId: pendoConfig.account.id,
    });
    return true;
  } catch (error) {
    console.error("[PENDO-WORDADDIN] ❌ Error initializing Pendo:", error);
    return false;
  }
};

/**
 * Initialize Pendo from localStorage UserData
 * If UserData is not available, initializes with anonymous data to ensure events are sent
 * Convenience function that reads user data from localStorage
 */
export const initializePendoFromStorage = () => {
  try {
    const userDataStr = localStorage.getItem("UserData");
    
    if (!userDataStr) {
      console.log("[PENDO-WORDADDIN] ⚠️ No UserData in localStorage, initializing Pendo with anonymous data");
      // Initialize with anonymous data to ensure events are sent
      // This will be updated when UserData becomes available
      return initializePendo({}, {});
    }

    const userData = JSON.parse(userDataStr);
    
    // Extract organization info if available
    const organizationData = {
      name: userData.organizationName || userData.organization || '',
      id: userData.organizationId || userData.orgId || '',
    };

    return initializePendo(userData, organizationData);
  } catch (error) {
    console.error("[PENDO-WORDADDIN] ❌ Error initializing Pendo from storage:", error);
    // Even on error, try to initialize with anonymous data
    try {
      return initializePendo({}, {});
    } catch (fallbackError) {
      console.error("[PENDO-WORDADDIN] ❌ Failed to initialize Pendo with anonymous data:", fallbackError);
      return false;
    }
  }
};

/**
 * Retry Pendo initialization when UserData becomes available
 * Call this after UserData is set in localStorage
 */
export const retryPendoInitialization = () => {
  try {
    const userDataStr = localStorage.getItem("UserData");
    if (userDataStr) {
      console.log("[PENDO-WORDADDIN] 🔄 Retrying Pendo initialization with UserData");
      const userData = JSON.parse(userDataStr);
      
      const organizationData = {
        name: userData.organizationName || userData.organization || '',
        id: userData.organizationId || userData.orgId || '',
      };

      return initializePendo(userData, organizationData);
    }
    return false;
  } catch (error) {
    console.error("[PENDO-WORDADDIN] ❌ Error retrying Pendo initialization:", error);
    return false;
  }
};

/**
 * Check if Pendo is initialized and ready
 * @returns {boolean} - True if Pendo is initialized
 */
const isPendoInitialized = () => {
  try {
    if (typeof window !== 'undefined' && window.pendo) {
      // Check if Pendo has isReady method and it returns true
      if (typeof window.pendo.isReady === 'function') {
        return window.pendo.isReady();
      }
      // Fallback: check if visitor ID exists and is not a temporary ID
      if (typeof window.pendo.getVisitorId === 'function') {
        const visitorId = window.pendo.getVisitorId();
        // Temporary IDs start with _PENDO_T_
        return visitorId && !visitorId.startsWith('_PENDO_T_');
      }
      // If neither method exists, assume initialized if pendo exists
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

/**
 * Track a Pendo event with optional metadata
 * Ensures Pendo is initialized before tracking, or initializes with anonymous data
 * @param {string} eventName - The name of the event to track
 * @param {Object} metadata - Optional metadata to include with the event
 * @returns {boolean} - True if event was tracked successfully
 */
export const trackPendoEvent = (eventName, metadata = {}) => {
  try {
    if (!isPendoAvailable()) {
      console.log(`[PENDO-WORDADDIN] ⚠️ Pendo not available. Event would be: ${eventName}`, metadata);
      return false;
    }

    // Ensure Pendo is initialized before tracking
    if (!isPendoInitialized()) {
      console.log(`[PENDO-WORDADDIN] ⚠️ Pendo not initialized, initializing with available data before tracking: ${eventName}`);
      // Try to initialize with UserData, or anonymous data as fallback
      initializePendoFromStorage();
    }

    window.pendo.track(eventName, metadata);
    console.log(`[PENDO-WORDADDIN] ✅ Event tracked: ${eventName}`, metadata);
    return true;
  } catch (error) {
    console.error(`[PENDO-WORDADDIN] ❌ Error tracking event ${eventName}:`, error);
    return false;
  }
};

/**
 * Track when user creates a new legal reference (Addon event)
 * Used when user enters title and optional URL in the New Reference form
 * @param {Object} referenceData - Data about the created reference
 * @param {string} referenceData.title - The title of the reference
 * @param {string} referenceData.url - The URL of the reference (optional)
 * @param {boolean} referenceData.isFootnote - Whether it was added as footnote or additional resource
 */
export const trackCreateNewReference = (referenceData = {}) => {
  const metadata = {
    action: 'create_new_reference',
    title: referenceData.title || '',
    hasUrl: !!referenceData.url,
    referenceType: referenceData.isFootnote ? 'footnote' : 'additional_resource',
    timestamp: new Date().toISOString(),
  };
  
  console.log(`[PENDO-WORDADDIN] 📝 Tracking new reference creation: ${PENDO_EVENTS.ADDON}`);
  return trackPendoEvent(PENDO_EVENTS.ADDON, metadata);
};

/**
 * Track when user adds existing legal references or policies (CreateReference event)
 * Used when user selects references from Legal Reference tab or Policies tab
 * @param {Object} referenceData - Data about the added references
 * @param {string} referenceData.source - 'legal_reference' or 'policy'
 * @param {number} referenceData.count - Number of references added
 * @param {boolean} referenceData.isFootnote - Whether added as footnote or additional resource
 * @param {Array} referenceData.referenceNames - Names of the references (optional)
 */
export const trackAddExistingReference = (referenceData = {}) => {
  const metadata = {
    action: 'add_existing_reference',
    source: referenceData.source || 'unknown',
    count: referenceData.count || 1,
    referenceType: referenceData.isFootnote ? 'footnote' : 'additional_resource',
    referenceNames: referenceData.referenceNames || [],
    timestamp: new Date().toISOString(),
  };
  
  console.log(`[PENDO-WORDADDIN] 📋 Tracking existing reference addition: ${PENDO_EVENTS.CREATE_REFERENCE}`);
  return trackPendoEvent(PENDO_EVENTS.CREATE_REFERENCE, metadata);
};

/**
 * Track when user deletes an inline reference from the document
 * @param {Object} deleteData - Data about the deleted reference
 * @param {number} deleteData.refNumber - The reference number that was deleted
 * @param {boolean} deleteData.success - Whether deletion was successful
 * @param {string} deleteData.message - Additional message about the deletion
 */
export const trackDeleteReference = (deleteData = {}) => {
  try {
    console.log(`[PENDO-WORDADDIN] 🗑️ trackDeleteReference called with:`, deleteData);
    
    const metadata = {
      action: 'delete_reference',
      refNumber: deleteData.refNumber || null,
      success: deleteData.success !== undefined ? deleteData.success : true,
      message: deleteData.message || '',
      timestamp: new Date().toISOString(),
    };
    
    console.log(`[PENDO-WORDADDIN] 🗑️ Tracking reference deletion: ${PENDO_EVENTS.DELETE_REFERENCE}`, metadata);
    const result = trackPendoEvent(PENDO_EVENTS.DELETE_REFERENCE, metadata);
    console.log(`[PENDO-WORDADDIN] 🗑️ DeleteReference event tracking result:`, result);
    return result;
  } catch (error) {
    console.error(`[PENDO-WORDADDIN] ❌ Error in trackDeleteReference:`, error);
    return false;
  }
};

export default {
  PENDO_EVENTS,
  initializePendo,
  initializePendoFromStorage,
  retryPendoInitialization,
  trackPendoEvent,
  trackCreateNewReference,
  trackAddExistingReference,
  trackDeleteReference,
};

